import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { Footer } from '../components/Footer';
import { ZapIcon, FlameIcon, LeafIcon, PlugIcon, GaugeIcon, ShieldCheckIcon } from 'lucide-react';
const ionityFeatures = [{
  icon: ZapIcon,
  title: 'Hochleistungs-Schnellladen',
  desc: 'Bis zu 350 kW Ladeleistung für ultraschnelles Laden Ihres Elektrofahrzeugs.'
}, {
  icon: LeafIcon,
  title: '100% Ökostrom',
  desc: 'Alle IONITY-Ladepunkte werden ausschließlich mit erneuerbarer Energie betrieben.'
}, {
  icon: PlugIcon,
  title: 'Alle gängigen Stecker',
  desc: 'CCS-Stecker kompatibel mit allen aktuellen Elektrofahrzeugen.'
}];
const lngFeatures = [{
  icon: FlameIcon,
  title: 'Flüssigerdgas (LNG)',
  desc: 'Moderne LNG-Betankung für Schwerlast-LKW – die saubere Alternative zu Diesel.'
}, {
  icon: GaugeIcon,
  title: 'Schnelle Betankung',
  desc: 'Effiziente Betankung in wenigen Minuten – kein langes Warten.'
}, {
  icon: ShieldCheckIcon,
  title: 'Sichere Anlage',
  desc: 'Modernste Sicherheitsstandards für eine zuverlässige und sichere Betankung.'
}];
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function ChargingPage() {
  return <>
      <PageHeader title="LNG & IONITY Laden" subtitle="Die Energie der Zukunft – heute verfügbar." image="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=1200&q=80" accentColor="text-transparent bg-clip-text bg-gradient-to-r from-white to-neon-green" accentGlow="bg-neon-green" data-id="element-382" />

      <section className="py-20 px-4 bg-base" data-id="element-383">
        <div className="max-w-7xl mx-auto" data-id="element-384">
          {/* IONITY Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20" data-id="element-385">
            <div className="flex items-center gap-4 mb-8" data-id="element-386">
              <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center" data-id="element-387">
                <ZapIcon className="w-6 h-6 text-neon-green" data-id="element-388" />
              </div>
              <h2 className="text-4xl font-heading font-bold text-white" data-id="element-389">
                IONITY Ladestation
              </h2>
            </div>
            <p className="text-lg text-white/60 leading-relaxed max-w-3xl mb-12" data-id="element-390">
              Laden Sie Ihr Elektrofahrzeug an unserer IONITY
              Hochleistungs-Ladestation. Mit bis zu 350 kW Ladeleistung sind Sie
              in kürzester Zeit wieder startklar – und das mit 100% erneuerbarer
              Energie.
            </p>
            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
            once: true
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-391">
              {ionityFeatures.map(f => {
              const Icon = f.icon;
              return <motion.div key={f.title} variants={fadeUp} className="glass-card p-8 rounded-2xl border-neon-green/10" data-id="element-392">
                    <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mb-5" data-id="element-393">
                      <Icon className="w-6 h-6 text-neon-green" strokeWidth={1.5} data-id="element-394" />
                    </div>
                    <h3 className="text-xl font-heading font-bold mb-2 text-white" data-id="element-395">
                      {f.title}
                    </h3>
                    <p className="text-white/60" data-id="element-396">{f.desc}</p>
                  </motion.div>;
            })}
            </motion.div>
          </motion.div>

          {/* LNG Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-397">
            <div className="flex items-center gap-4 mb-8" data-id="element-398">
              <div className="w-12 h-12 rounded-xl bg-blue-400/10 flex items-center justify-center" data-id="element-399">
                <FlameIcon className="w-6 h-6 text-blue-400" data-id="element-400" />
              </div>
              <h2 className="text-4xl font-heading font-bold text-white" data-id="element-401">
                LNG-Säule
              </h2>
            </div>
            <p className="text-lg text-white/60 leading-relaxed max-w-3xl mb-12" data-id="element-402">
              Unsere LNG-Tankstelle bietet Flüssigerdgas für moderne
              Schwerlast-LKW. Eine saubere und effiziente Alternative zu
              herkömmlichem Diesel – rund um die Uhr verfügbar.
            </p>
            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
            once: true
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-403">
              {lngFeatures.map(f => {
              const Icon = f.icon;
              return <motion.div key={f.title} variants={fadeUp} className="glass-card p-8 rounded-2xl border-blue-400/10" data-id="element-404">
                    <div className="w-12 h-12 rounded-xl bg-blue-400/10 flex items-center justify-center mb-5" data-id="element-405">
                      <Icon className="w-6 h-6 text-blue-400" strokeWidth={1.5} data-id="element-406" />
                    </div>
                    <h3 className="text-xl font-heading font-bold mb-2 text-white" data-id="element-407">
                      {f.title}
                    </h3>
                    <p className="text-white/60" data-id="element-408">{f.desc}</p>
                  </motion.div>;
            })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-409" />
    </>;
}